import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { outletService } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Bandage,
  Plus,
  Edit,
  Trash2,
  CheckCircle2,
  XCircle,
  Globe,
  Mail,
  MessageSquare,
  Settings,
  LogOut,
  Eye,
  EyeOff,
  TestTube,
} from 'lucide-react';

const AdminPanel = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    outlet_type: 'wordpress',
    category: 'external',
    priority: 'C',
    themes: [],
    languages: ['fr'],
    seo_power: 5,
    geo_power: 5,
    reputation_power: 5,
    llm_visibility: 5,
    google_occupation: 5,
    publication_control: 5,
    overall_score: 50,
    cost_per_publication: null,
    pitch: '',
    connection_mode: 'manual_premium',
    wordpress_url: '',
    wordpress_username: '',
    wordpress_app_password: '',
    email_address: '',
    whatsapp_number: '',
    human_validation: false,
    requires_admin_approval: false,
    active: true,
    notes: '',
  });

  const loadOutlets = useCallback(async () => {
    try {
      const data = await outletService.getAllOutletsAdmin();
      setOutlets(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des outlets');
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadOutlets();
  }, [isAdmin, navigate, loadOutlets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert themes from comma-separated string to array
      const themes = typeof formData.themes === 'string' 
        ? formData.themes.split(',').map(t => t.trim()).filter(t => t)
        : formData.themes;

      const payload = {
        ...formData,
        themes,
        accepted_topics: themes,
        overall_score: parseFloat(formData.overall_score),
        seo_power: formData.seo_power ? parseFloat(formData.seo_power) : null,
        geo_power: formData.geo_power ? parseFloat(formData.geo_power) : null,
        reputation_power: formData.reputation_power ? parseFloat(formData.reputation_power) : null,
        llm_visibility: formData.llm_visibility ? parseFloat(formData.llm_visibility) : null,
        google_occupation: formData.google_occupation ? parseFloat(formData.google_occupation) : null,
        publication_control: formData.publication_control ? parseFloat(formData.publication_control) : null,
      };

      if (editingOutlet) {
        await outletService.updateGlobalOutlet(editingOutlet.id, payload);
        toast.success('Outlet mis à jour avec succès');
      } else {
        await outletService.createGlobalOutlet(payload);
        toast.success('Outlet créé avec succès');
      }

      setShowForm(false);
      setEditingOutlet(null);
      resetForm();
      loadOutlets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (outlet) => {
    setEditingOutlet(outlet);
    setFormData({
      name: outlet.name,
      url: outlet.url || '',
      outlet_type: outlet.outlet_type,
      category: outlet.category,
      priority: outlet.priority,
      themes: Array.isArray(outlet.themes) ? outlet.themes.join(', ') : '',
      languages: outlet.languages || ['fr'],
      seo_power: outlet.seo_power || 5,
      geo_power: outlet.geo_power || 5,
      reputation_power: outlet.reputation_power || 5,
      llm_visibility: outlet.llm_visibility || 5,
      google_occupation: outlet.google_occupation || 5,
      publication_control: outlet.publication_control || 5,
      overall_score: outlet.overall_score || 50,
      pitch: outlet.pitch || '',
      connection_mode: outlet.connection_mode,
      wordpress_url: outlet.wordpress_url || '',
      wordpress_username: outlet.wordpress_username || '',
      wordpress_app_password: outlet.wordpress_app_password || '',
      email_address: outlet.email_address || '',
      whatsapp_number: outlet.whatsapp_number || '',
      human_validation: outlet.human_validation || false,
      requires_admin_approval: outlet.requires_admin_approval || false,
      active: outlet.active,
      notes: outlet.notes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (outletId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet outlet ?')) return;

    try {
      await outletService.deleteOutlet(outletId);
      toast.success('Outlet supprimé');
      loadOutlets();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTest = async (outletId) => {
    try {
      const result = await outletService.testOutlet(outletId);
      if (result.success) {
        toast.success('Connexion réussie !');
      } else {
        toast.error(`Échec : ${result.error}`);
      }
    } catch (error) {
      toast.error('Erreur lors du test');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      outlet_type: 'wordpress',
      category: 'external',
      priority: 'C',
      themes: [],
      languages: ['fr'],
      seo_power: 5,
      geo_power: 5,
      reputation_power: 5,
      llm_visibility: 5,
      google_occupation: 5,
      publication_control: 5,
      overall_score: 50,
      pitch: '',
      connection_mode: 'manual_premium',
      wordpress_url: '',
      wordpress_username: '',
      wordpress_app_password: '',
      email_address: '',
      whatsapp_number: '',
      human_validation: false,
      requires_admin_approval: false,
      active: true,
      notes: '',
    });
  };

  const filteredOutlets = outlets.filter(outlet => {
    if (filterPriority !== 'all' && outlet.priority !== filterPriority) return false;
    if (filterCategory !== 'all' && outlet.category !== filterCategory) return false;
    return true;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'S': return 'destructive';
      case 'A': return 'default';
      case 'B': return 'secondary';
      case 'C': return 'outline';
      default: return 'outline';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'owned': return '🏢';
      case 'network': return '🌐';
      case 'external': return '📧';
      case 'premium': return '⭐';
      default: return '📄';
    }
  };

  const getConnectionIcon = (mode) => {
    switch (mode) {
      case 'wordpress': return <Globe className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      case 'whatsapp': return <MessageSquare className="h-4 w-4" />;
      case 'manual_premium': return <Bandage className="h-4 w-4" />;
      default: return <Settings className="h-4 w-4" />;
    }
  };

  if (loading && outlets.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-2">
              <Bandage className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold">AUTO-RÉPUTATION</span>
              <Badge variant="destructive">Admin</Badge>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{user?.email}</span>
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
              <Button variant="ghost" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Débouchés</h1>
          <p className="text-gray-600 mt-1">Base confidentielle des outlets - {outlets.length} total</p>
        </div>

        {/* Filters & Actions */}
        <div className="mb-6 flex gap-4 items-center flex-wrap">
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="all">Toutes priorités</option>
            <option value="S">Priority S (Premium)</option>
            <option value="A">Priority A (Owned)</option>
            <option value="B">Priority B (Network)</option>
            <option value="C">Priority C (External)</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="all">Toutes catégories</option>
            <option value="owned">Owned</option>
            <option value="network">Network</option>
            <option value="external">External</option>
            <option value="premium">Premium</option>
          </select>

          <Button onClick={() => { setShowForm(true); setEditingOutlet(null); resetForm(); }} data-testid="add-outlet-btn">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un outlet
          </Button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{editingOutlet ? 'Modifier' : 'Nouveau'} Outlet</CardTitle>
              <CardDescription>
                {editingOutlet ? 'Modifiez les informations de l\'outlet' : 'Ajoutez un nouveau débouché à la base confidentielle'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      placeholder="Economie Matin"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="url">URL</Label>
                    <Input
                      id="url"
                      value={formData.url}
                      onChange={(e) => setFormData({...formData, url: e.target.value})}
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>

                {/* Category & Priority */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Catégorie *</Label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    >
                      <option value="owned">Owned</option>
                      <option value="network">Network</option>
                      <option value="external">External</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="priority">Priorité *</Label>
                    <select
                      id="priority"
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    >
                      <option value="S">S (Premium)</option>
                      <option value="A">A (High)</option>
                      <option value="B">B (Medium)</option>
                      <option value="C">C (Low)</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="outlet_type">Type *</Label>
                    <select
                      id="outlet_type"
                      value={formData.outlet_type}
                      onChange={(e) => setFormData({...formData, outlet_type: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    >
                      <option value="wordpress">WordPress</option>
                      <option value="email">Email</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="manual">Manual</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="connection_mode">Mode connexion *</Label>
                    <select
                      id="connection_mode"
                      value={formData.connection_mode}
                      onChange={(e) => setFormData({...formData, connection_mode: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                    >
                      <option value="wordpress">WordPress API</option>
                      <option value="email">Email</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="api">API dédiée</option>
                      <option value="manual_premium">Opérateur humain</option>
                    </select>
                  </div>
                </div>

                {/* Themes & Languages */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="themes">Thèmes (séparés par virgule)</Label>
                    <Input
                      id="themes"
                      value={formData.themes}
                      onChange={(e) => setFormData({...formData, themes: e.target.value})}
                      placeholder="economie, finance, entreprise"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="languages">Langues</Label>
                    <Input
                      id="languages"
                      value={formData.languages.join(', ')}
                      onChange={(e) => setFormData({...formData, languages: e.target.value.split(',').map(l => l.trim())})}
                      placeholder="fr, en, es"
                    />
                  </div>
                </div>

                {/* Scores */}
                <div className="space-y-2">
                  <Label>Scores (0-10) & Rentabilité</Label>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="seo_power" className="text-xs">SEO Power</Label>
                      <Input
                        id="seo_power"
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={formData.seo_power}
                        onChange={(e) => setFormData({...formData, seo_power: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="geo_power" className="text-xs">GEO Power</Label>
                      <Input
                        id="geo_power"
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={formData.geo_power}
                        onChange={(e) => setFormData({...formData, geo_power: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="overall_score" className="text-xs">Overall Score (0-100)</Label>
                      <Input
                        id="overall_score"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.overall_score}
                        onChange={(e) => setFormData({...formData, overall_score: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="cost_per_publication" className="text-xs">Coût/publication (€)</Label>
                      <Input
                        id="cost_per_publication"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.cost_per_publication || ''}
                        onChange={(e) => setFormData({...formData, cost_per_publication: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Connection Details */}
                {formData.connection_mode === 'wordpress' && (
                  <div className="space-y-4 border-t pt-4">
                    <h4 className="font-medium">Configuration WordPress</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <Input
                        placeholder="WordPress URL"
                        value={formData.wordpress_url}
                        onChange={(e) => setFormData({...formData, wordpress_url: e.target.value})}
                      />
                      <Input
                        placeholder="Username"
                        value={formData.wordpress_username}
                        onChange={(e) => setFormData({...formData, wordpress_username: e.target.value})}
                      />
                      <Input
                        type="password"
                        placeholder="App Password"
                        value={formData.wordpress_app_password}
                        onChange={(e) => setFormData({...formData, wordpress_app_password: e.target.value})}
                      />
                    </div>
                  </div>
                )}

                {formData.connection_mode === 'email' && (
                  <div className="space-y-2 border-t pt-4">
                    <Label htmlFor="email_address">Email Address</Label>
                    <Input
                      id="email_address"
                      type="email"
                      value={formData.email_address}
                      onChange={(e) => setFormData({...formData, email_address: e.target.value})}
                      placeholder="contact@example.com"
                    />
                  </div>
                )}

                {formData.connection_mode === 'whatsapp' && (
                  <div className="space-y-2 border-t pt-4">
                    <Label htmlFor="whatsapp_number">WhatsApp Number</Label>
                    <Input
                      id="whatsapp_number"
                      value={formData.whatsapp_number}
                      onChange={(e) => setFormData({...formData, whatsapp_number: e.target.value})}
                      placeholder="+33612345678"
                    />
                  </div>
                )}

                {/* Pitch & Notes */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pitch">Pitch</Label>
                    <Textarea
                      id="pitch"
                      value={formData.pitch}
                      onChange={(e) => setFormData({...formData, pitch: e.target.value})}
                      rows={3}
                      placeholder="Description du débouché..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes internes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={3}
                      placeholder="Notes confidentielles..."
                    />
                  </div>
                </div>

                {/* Flags */}
                <div className="flex gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.human_validation}
                      onChange={(e) => setFormData({...formData, human_validation: e.target.checked})}
                    />
                    <span className="text-sm">Validation humaine requise</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.requires_admin_approval}
                      onChange={(e) => setFormData({...formData, requires_admin_approval: e.target.checked})}
                    />
                    <span className="text-sm">Approbation admin requise</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    />
                    <span className="text-sm">Actif</span>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 border-t pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowForm(false); setEditingOutlet(null); resetForm(); }}
                  >
                    Annuler
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Enregistrement...' : (editingOutlet ? 'Mettre à jour' : 'Créer')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Outlets List */}
        <div className="grid gap-4">
          {filteredOutlets.map((outlet) => (
            <Card key={outlet.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{getCategoryIcon(outlet.category)}</span>
                      <h3 className="text-lg font-semibold">{outlet.name}</h3>
                      <Badge variant={getPriorityColor(outlet.priority)}>
                        Priority {outlet.priority}
                      </Badge>
                      <Badge variant="outline">{outlet.category}</Badge>
                      {outlet.human_validation && (
                        <Badge variant="warning">Human Validation</Badge>
                      )}
                      {!outlet.active && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                      <div>
                        <span className="font-medium">Score:</span> {outlet.overall_score}/100
                      </div>
                      <div className="flex items-center gap-1">
                        {getConnectionIcon(outlet.connection_mode)}
                        <span>{outlet.connection_mode}</span>
                      </div>
                      <div>
                        <span className="font-medium">Type:</span> {outlet.outlet_type}
                      </div>
                      <div>
                        <span className="font-medium">Langues:</span> {outlet.languages?.join(', ')}
                      </div>
                    </div>

                    {outlet.themes && outlet.themes.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {outlet.themes.map((theme) => (
                          <Badge key={`${outlet.id}-${theme}`} variant="secondary" className="text-xs">
                            {theme}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {outlet.url && (
                      <a
                        href={outlet.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {outlet.url}
                      </a>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {outlet.connection_mode === 'wordpress' && outlet.wordpress_username && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTest(outlet.id)}
                      >
                        <TestTube className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(outlet)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(outlet.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredOutlets.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center text-gray-500">
              Aucun outlet trouvé avec ces filtres
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
